import { useEffect, useState, useRef } from "react";
import { Rothko } from "web/script/modules/rothko";
import { URLQuery } from "web/script/utils/url";

export enum LOADING_STATES {
    DEFAULT,
    PENDING,
    COMPLETE,
    ERROR,
}

/*  Note - parameters needs to be a reference to an object between re-renders as useEffect does not do a deep object comparison when deciding
if it should execute. For example:
```
const rothkoOptions = useMemo(() =>({ slug: productSlug, uid: productUid }), [productSlug, productUid]);
const { data, loadingState } = useRothko(endpoint, rothkoOptions);
```
This will ensure parameters is always a reference to the same object
*/
export function useRothko<T = any>(
    endpoint: string,
    parameters?: URLQuery
): { data: T | undefined; loadingState: LOADING_STATES } {
    const clientRef = useRef<Rothko | undefined>(undefined);
    const [data, updateData] = useState<T | undefined>(undefined);
    const [loadingState, setLoadingState] = useState(LOADING_STATES.DEFAULT);

    useEffect(() => {
        let client = new Rothko(endpoint);
        client.on("data", (ev, response) => {
            updateData(response);
            setLoadingState(LOADING_STATES.COMPLETE);
        });
        clientRef.current = client;
    }, [endpoint]);

    useEffect(() => {
        setLoadingState(LOADING_STATES.PENDING);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        clientRef.current!.fetch(parameters).catch(() => setLoadingState(LOADING_STATES.ERROR));
    }, [parameters]);

    return { data, loadingState };
}
